import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { Header } from './Header';
import { Pagination } from './Pagination';
import { TableHead } from './TableHead';
import { TableBody } from './TableBody';
import type {
  ColumnOrderState,
  SortingState,
  FilterFn,
  ColumnPinningState,
  Row,
} from '@tanstack/react-table';
import { TableProvider } from '../providers/TableProvider';
import { EditableOnlyProvider } from './OptimizedTableProvider';
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  getFilteredRowModel,
  getExpandedRowModel,
  getSortedRowModel,
} from '@tanstack/react-table';
import { RowSelection, TableProps, TableVariant, type Values } from '../types';
import {
  createFillerColumn,
  createSelectorColumn,
  getTableMeta,
  getTotalHeadersSize,
  updateSorting,
  buildAllLeafColumnIds,
  normalizeColumnOrder,
  resolveSelectableColumnOrder,
} from '../utils';
import { DefaultCell } from './DefaultCell';
import { rankItem } from '@tanstack/match-sorter-utils';
import { Plus } from '@phosphor-icons/react';
import { omit, omitBy, size } from 'lodash';
import useLocalStorageState from 'use-local-storage-state';
import { useVirtual, useTableEvents, useFillerLockedColumnOrder } from '../hooks';
import { NavigationGuard } from './NavigationGuard';
import { useInitialHiddenColumns } from '../hooks';
import { EmptyState } from 'components';
import { TableLoader } from './TableLoader';
import type { View } from 'types/views';
import { deserializeTableSettings } from './views/tableSettings';

const TABLE_OVERSCAN = 150;
const TABLE_VARIANT_DEFAULT: TableVariant = 'default';

const minCellWidth = 96;
const maxCellWidth = 500;
const perPage = 20;
const oldRowHeights = { compact: 44, default: 64, comfortable: 80 };
const rowHeights = { compact: 32, default: 40, comfortable: 48 };

export interface TypeId {
  id?: string | number;
}

export const Table = <T extends TypeId>({
  rowSelection: rowSelectionProps,
  setRowSelection: setRowSelectionProps,
  getRowId,
  isShowHeaderEmptyData = true,
  initialHidden = [],
  initialVisible = [],
  headerAddButtonProps,
  getRowCanExpand,
  renderExpandedComponent,
  expandedRowHeight,
  variant = TABLE_VARIANT_DEFAULT,
  expandedRowBackgroundColor,
  getRowBackground,
  editableState: editableStateProp,
  setEditableState: setEditableStateProp,
  ...props
}: TableProps<T>) => {
  type EditableState = { rowIndex: number | null; columnId: string | null } | null;
  const [internalEditableState, setInternalEditableState] = useState<EditableState>(null);
  // True while the guard's confirmation dialog is open; editing cells read it to skip the
  // commit/exit triggered by the dialog stealing focus (DL-60097).
  const isNavBlockingRef = useRef(false);
  const editableState = editableStateProp ?? internalEditableState;
  const editableStateRef = useRef(editableState);
  editableStateRef.current = editableState ?? null;
  // Accepts a functional updater so an exiting cell can release the shared slot only when it
  // still owns it (prevents a blur-save clobbering a just-clicked target cell — DL-60264).
  const setEditableState = useCallback(
    (state: EditableState | ((prev: EditableState) => EditableState)) => {
      if (setEditableStateProp) {
        setEditableStateProp(state);
      } else {
        setInternalEditableState(state);
      }
    },
    [setEditableStateProp]
  );
  let rowHeight = props.rowHeight ? rowHeights[props.rowHeight] : rowHeights.default;
  if (props.config?.v1) {
    rowHeight = props.rowHeight ? oldRowHeights[props.rowHeight] : oldRowHeights.default;
  }
  const initialPin = props?.config?.row?.selectable
    ? { left: ['Selector', ...(props.initialPinned || [])], right: [] }
    : props.initialPinned
      ? { left: [...(props.initialPinned || [])], right: [] }
      : { left: [], right: [] };

  const columnIds = props.columns?.map(
    col =>
      col.id ??
      ('accessorKey' in col && typeof col.accessorKey === 'string' ? col.accessorKey : undefined)
  );

  const defaultColumnOrder = useMemo((): ColumnOrderState => {
    const dataIds = columnIds?.filter((id): id is string => id !== undefined) ?? [];
    const isSelectable = Boolean(props?.config?.row?.selectable);
    const allLeaf = buildAllLeafColumnIds(dataIds, isSelectable);
    const base = resolveSelectableColumnOrder([], isSelectable, dataIds);
    return normalizeColumnOrder(base, allLeaf);
  }, [props?.config?.row?.selectable, columnIds]);

  const initialColumnVisibility = useMemo(() => {
    const visibility: Record<string, boolean> = {};
    // Explicitly hide columns listed in initialHidden
    for (const key of initialHidden) {
      if (key) visibility[key] = false;
    }
    if (initialVisible?.length > 0) {
      // Hide all columns not listed in initialVisible
      for (const id of columnIds) {
        if (id && !initialVisible?.includes(id)) {
          visibility[id] = false;
        }
      }
    }
    return visibility;
  }, [initialHidden, initialVisible, columnIds]);

  const [data, setData] = useState<T[]>(props.data);
  const [expanded, setExpanded] = useState({});
  const [sorting, setSorting] = useLocalStorageState<SortingState>(`${props.name}-tableSorting`, {
    defaultValue: [],
  });
  const [columnVisibility, setColumnVisibility] = useLocalStorageState(
    `${props.name}-tableColumnVisibility`,
    {
      defaultValue: initialColumnVisibility,
    }
  );
  useInitialHiddenColumns({ name: props.name, initialHidden, setColumnVisibility });
  const [columnOrder, setColumnOrder] = useLocalStorageState<ColumnOrderState>(
    `${props.name}-tableColumnOrder`,
    {
      defaultValue: defaultColumnOrder,
    }
  );
  const { effectiveColumnOrder, onColumnOrderChange } = useFillerLockedColumnOrder(
    columnOrder,
    setColumnOrder,
    columnIds,
    Boolean(props?.config?.row?.selectable)
  );
  const [columnPinning, setColumnPinning] = useLocalStorageState<ColumnPinningState>(
    `${props.name}-tableColumnPinned`,
    {
      defaultValue: initialPin,
    }
  );
  const effectiveColumnPinning = useMemo((): ColumnPinningState => {
    if (!props?.config?.row?.selectable) return columnPinning;
    const left = columnPinning.left ?? [];
    if (left[0] === 'Selector') return columnPinning;
    return { ...columnPinning, left: ['Selector', ...left.filter(id => id !== 'Selector')] };
  }, [props?.config?.row?.selectable, columnPinning]);

  const [columnSizing, setColumnSizing] = useLocalStorageState(`${props.name}-tableColumnSizing`, {
    defaultValue: {},
  });
  const [globalFilter, setGlobalFilter] = useState('');
  const [rowSelectionInside, setRowSelectionInside] = useState<RowSelection>({});

  const isControlledRowSelection =
    rowSelectionProps !== undefined && typeof setRowSelectionProps === 'function';

  const rowSelection = useMemo(() => {
    return isControlledRowSelection ? rowSelectionProps : rowSelectionInside;
  }, [isControlledRowSelection, rowSelectionProps, rowSelectionInside]);

  const setRowSelection = useCallback(
    (updater: React.SetStateAction<RowSelection>) => {
      if (isControlledRowSelection) {
        setRowSelectionProps(updater);
      } else {
        setRowSelectionInside(updater);
      }
    },
    [isControlledRowSelection, setRowSelectionProps, setRowSelectionInside]
  );

  const columnResizeMode = 'onChange';
  const tableContainerRef = useRef<HTMLDivElement>(null);

  const selectorColumn = createSelectorColumn<T>(
    props?.config?.row?.selectable,
    props?.config?.isExpandable,
    props?.config?.subRowsDepth,
    props.config?.v1,
    props.config?.event
  );

  const defaultColumn = {
    minSize: minCellWidth,
    maxSize: maxCellWidth,
    cell: DefaultCell<T>,
  };

  const fillerColumn = useMemo(() => createFillerColumn<T>(), []);

  const cols = useMemo(
    () => [...selectorColumn, ...props.columns, fillerColumn],
    [selectorColumn, props.columns, fillerColumn]
  );

  const fuzzyFilter: FilterFn<T> = (row, columnId, value, addMeta) => {
    const itemRank = rankItem(row.getValue(columnId), value);

    addMeta(itemRank);

    return itemRank.passed;
  };

  useTableEvents(
    columnVisibility,
    effectiveColumnOrder,
    effectiveColumnPinning,
    columnSizing,
    rowSelection,
    props.config?.event
  );

  const table = useReactTable({
    getRowId: getRowId ? getRowId : (row, index) => String(row.id ?? index),
    data: props.data,
    defaultColumn: props.defaultColumn ? props.defaultColumn : defaultColumn,
    columns: cols,
    getRowCanExpand,
    ...(props.onPaginationChange
      ? {
          manualPagination: true,
          pageCount: props.pageCount || -1,
          onPaginationChange: updater => {
            if (typeof updater !== 'function') return;
            const newPageInfo = updater(table.getState().pagination);
            props.onPaginationChange?.(newPageInfo);
          },
        }
      : {
          initialState: {
            pagination: {
              pageIndex: props.config?.pagination?.pageIndex
                ? props.config.pagination.pageIndex
                : 0,
              pageSize: props.config?.pagination ? props.config.pagination.pageSize : perPage,
            },
          },
        }),
    state: {
      expanded: {
        ...expanded,
        paginateExpandedRows: Boolean(props.onPaginationChange),
      },
      columnOrder: effectiveColumnOrder,
      sorting,
      columnPinning: effectiveColumnPinning,
      columnSizing,
      columnVisibility,
      globalFilter: props.meta?.tableState ? undefined : globalFilter,
      rowSelection,
      ...(props.onPaginationChange
        ? {
            pagination: props.config?.pagination,
            sorting: props.meta?.tableState?.sort
              ? [{ id: props.meta.tableState.sort, desc: props.meta.tableState.order === 'DESC' }]
              : undefined,
          }
        : {}),
    },
    columnResizeMode: 'onChange',
    enableRowSelection: row => {
      return row?.original.id !== -1;
    },
    globalFilterFn: props?.fuzzyFilter ? props.fuzzyFilter : fuzzyFilter,
    paginateExpandedRows: Boolean(props.onPaginationChange),
    getSubRows: row =>
      (row as T & { subRows: T[] })?.subRows ||
      (row as T & Record<string, unknown>)?.[props?.config?.getSubRowsKey ?? ''] ||
      [],
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onRowSelectionChange: setRowSelection,
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: updater => {
      if (typeof updater !== 'function') return;
      const sortArray = updater(table.getState().sorting);
      const sortState = updateSorting(sortArray, props.meta?.tableState);
      if (props.config?.event) {
        props.config.event('sorting', {
          sort: sortState.sort || '',
          order: sortState.order || '',
        });
      }
      if (table.options.meta?.onTableStateChange && Boolean(props.onPaginationChange)) {
        table.options.meta?.onTableStateChange({
          sort: sortState.sort,
          order: sortState.order,
          page: null,
          cursor: null,
        });
        return;
      }
      setSorting(sortArray);
    },
    manualSorting: Boolean(props.onPaginationChange),
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange,
    onExpandedChange: setExpanded,
    onColumnPinningChange: setColumnPinning,
    onColumnSizingChange: setColumnSizing,
    meta: {
      ...getTableMeta(
        setData,
        props.meta,
        Object.entries(rowSelection)
          .filter(([, selected]) => selected)
          .map(([rowId]) => rowId)
      ),
    },
    debugTable: false,
  });

  //  table width
  const tableWidth = getTotalHeadersSize(table);

  // Get the row model for virtualization
  const rows = table.getRowModel().rows;

  // Create virtual items that include expanded rows
  const virtualRows = useMemo(() => {
    if (!renderExpandedComponent) {
      return rows;
    }
    const virtualItems: (
      | (typeof rows)[0]
      | { isExpandedRow: true; parentRow: (typeof rows)[0] }
    )[] = [];

    rows.forEach(row => {
      virtualItems.push(row);
      if (row.getIsExpanded()) {
        // Insert expanded row immediately after the parent row
        virtualItems.push({ isExpandedRow: true, parentRow: row });
      }
    });

    return virtualItems;
  }, [rows, renderExpandedComponent]);

  // Memoize the renderExpandedComponent to prevent unnecessary re-renders
  const memoizedRenderExpandedComponent = useCallback(
    (row?: Row<T>) => {
      return renderExpandedComponent?.(row);
    },
    [renderExpandedComponent]
  );

  const { virtualItems, totalSize } = useVirtual<T>(
    virtualRows,
    tableContainerRef,
    props.config?.disableVirtual,
    rowHeight,
    expandedRowHeight,
    TABLE_OVERSCAN
  );

  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const remainingWidth = useMemo(() => {
    const width = scrollContainerRef.current?.getBoundingClientRect().width;
    if (!width) {
      return 0;
    }
    const diff = width - table.getTotalSize();
    return Math.max(diff, 0);
  }, [scrollContainerRef.current, table]);

  useEffect(() => {
    setData(props.data);
  }, [props.data]);

  useEffect(() => {
    if (props.onPaginationChange) return;
    const configuredPageSize = props.config?.pagination?.pageSize;
    if (configuredPageSize !== null && configuredPageSize !== undefined) {
      if (table.getState().pagination.pageSize === configuredPageSize) return;
      table.setPageSize(configuredPageSize);
    }
  }, [props.config?.pagination?.pageSize, props.onPaginationChange]);

  const zeroRows = data.length === 0 && props.data.length === 0 && !props.isLoading;
  const hasActiveFilters =
    size(
      omitBy(omit({ ...props.meta?.tableState }, ['order', 'sort']), props?.excludedKeysEmptyCheck)
    ) > 0;
  const isNoResults = hasActiveFilters;
  const isNoData = !hasActiveFilters;
  const showNavButtons = table.getCanNextPage() || table.getCanPreviousPage();
  const showTableHead = !zeroRows || isShowHeaderEmptyData;

  // When a saved view is applied via the ViewDropdown, decode its column layout
  // (`_ts`) and apply it through the React state setters. Calling
  // `useLocalStorageState`'s setter (rather than writing localStorage directly)
  // is what guarantees the table re-renders with the new visibility/pinning/
  // order/sizing — direct localStorage writes from another component do not
  // notify in-tab consumers of the same key.
  const parentOnViewChange = props.onViewChange;
  const handleViewChange = useCallback(
    (view: View | null) => {
      if (view) {
        const tsString =
          typeof view.filterCriteria?._ts === 'string' ? view.filterCriteria._ts : '';
        if (tsString) {
          const next = deserializeTableSettings(tsString);
          setColumnVisibility(() => {
            // ensure all initialHidden columns are hidden if not defined in the view
            const updates: Record<string, boolean> = {};
            for (const columnId of initialHidden) {
              if (columnId && next.columnVisibility[columnId] === undefined) {
                updates[columnId] = false;
              }
            }
            return Object.keys(updates).length > 0
              ? { ...next.columnVisibility, ...updates }
              : next.columnVisibility;
          });
          setColumnOrder(next.columnOrder);
          setColumnPinning(next.columnPinning);
          setColumnSizing(next.columnSizing);
        }
      } else {
        // "Show all" — restore the initial column state so custom fields and
        // other initially-hidden columns are correctly hidden again.
        setColumnVisibility(initialColumnVisibility);
        setColumnOrder(defaultColumnOrder);
        setColumnPinning(initialPin);
        setColumnSizing({});
      }
      parentOnViewChange?.(view);
    },
    [
      setColumnVisibility,
      setColumnOrder,
      setColumnPinning,
      setColumnSizing,
      columnIds,
      defaultColumnOrder,
      initialHidden,
      initialPin,
      parentOnViewChange,
    ]
  );

  // Block navigation whenever any cell is in edit mode. `editableState.rowIndex` is set by
  // every editable cell on entry and cleared on exit, so it is the single source of truth
  // (DL-60097).
  const shouldBlock = editableState?.rowIndex != null;
  const clearEditing = useCallback(() => {
    setEditableState({ rowIndex: null, columnId: null });
  }, [setEditableState]);

  const editableValue = useMemo(
    () => ({
      editableState: editableState ?? null,
      editableStateRef,
      setEditableState,
      isNavBlockingRef,
    }),
    [editableState, setEditableState]
  );

  return (
    <TableProvider<T>
      value={{
        config: props.config ? { ...props.config, tableId: props.name } : {},
        table,
        globalFilter,
        setGlobalFilter,
        onTableStateChange: props.meta?.onTableStateChange,
        variant,
        expandedRowBackgroundColor,
        getRowBackground: getRowBackground as Values<T>['getRowBackground'],
        rowHeight,
      }}
    >
      <EditableOnlyProvider value={editableValue}>
        <NavigationGuard
          shouldBlock={shouldBlock}
          onProceed={clearEditing}
          navBlockingRef={isNavBlockingRef}
        />
        <TableWrapper
          data-testid={
            (props as TableProps<T> & { 'data-testid'?: string })['data-testid'] ||
            `table-${props.name}`
          }
        >
          {!props.config?.hideDefaultControlsHeader && (
            <Header
              addButtonProps={headerAddButtonProps}
              filterTitle={props?.filterTitle}
              rowSelectionCount={props.rowSelectionCount ?? Object.keys(rowSelection).length}
              addButtonName={zeroRows && isNoData ? undefined : props.config?.texts?.addButtonName}
              tableId={props?.name}
              tableName={props.name}
              filters={props.filters}
              viewConfig={props.viewConfig}
              onViewChange={handleViewChange}
              useFloatingBulkBar={props.useFloatingBulkBar ?? !props.config?.row?.selectable}
              extra={props.headerExtra}
            />
          )}
          <OuterContainer id="megayaee" ref={scrollContainerRef} $variant={variant}>
            {zeroRows ? (
              <OverLay abs $headerHeight={rowHeight}>
                <EmptyStateScrollRegion>
                  <EmptyStateInnerWrap>
                    <EmptyState
                      type={isNoResults ? 'noResults' : 'noData'}
                      img={
                        isNoResults
                          ? props.config?.texts?.noResultsImg
                          : props.config?.texts?.noDataImg
                      }
                      title={
                        isNoResults
                          ? props.config?.texts?.noResultsTitle
                          : props.config?.texts?.noDataTitle
                      }
                      description={
                        isNoResults
                          ? props.config?.texts?.noResultsDescription
                          : props.config?.texts?.noDataDescription
                      }
                      showAction={isNoData && Boolean(props?.config?.texts?.addButtonName)}
                      addRow={isNoData ? table.options?.meta?.addRow || '' : undefined}
                      actionProps={
                        isNoData
                          ? {
                              startIcon: <Plus size={16} color="white" />,
                              children: props?.config?.texts?.addButtonName,
                              variant: 'primary',
                              size: 'md',
                            }
                          : undefined
                      }
                      primaryOnClick={() =>
                        props.config?.event?.('addRow', { source_location: 'empty_state' })
                      }
                    />
                  </EmptyStateInnerWrap>
                </EmptyStateScrollRegion>
              </OverLay>
            ) : null}
            <Container
              ref={tableContainerRef}
              id={props.name}
              data-testid={`table_${props.name}_container`}
              $isEmpty={data.length === 0 && props.data.length === 0 && !props.isLoading}
            >
              <TableStyles
                style={{ minWidth: tableWidth }}
                rowHeight={rowHeight}
                fillerPosition={table.getTotalSize()}
                remainingWidth={remainingWidth}
                data-testid={`table_${props.name}_table`}
              >
                {showTableHead ? (
                  <TableHead
                    columnResizeMode={columnResizeMode}
                    isExpandable={props?.config?.isExpandable}
                    rowHeight={rowHeight}
                    v1={props?.config?.v1}
                  />
                ) : null}
                {props.isLoading ? (
                  <TableLoader
                    top={rowHeight}
                    columns={table.getVisibleLeafColumns().length}
                    variant={variant}
                  />
                ) : null}
                {data.length > 0 && (
                  <TableBody
                    virtualItems={virtualItems}
                    totalSize={totalSize}
                    rowHeight={rowHeight}
                    {...(renderExpandedComponent
                      ? { renderExpandedComponent: memoizedRenderExpandedComponent, virtualRows }
                      : {})}
                  />
                )}
              </TableStyles>
            </Container>

            {showNavButtons && props?.config?.pagination && !props.isLoading && data.length > 0 ? (
              <Pagination
                showNavButtons={showNavButtons}
                onPageChange={() => {
                  if (tableContainerRef?.current) {
                    tableContainerRef.current.scrollTo({
                      top: 0,
                      behavior: 'smooth',
                    });
                  }
                }}
              />
            ) : null}
          </OuterContainer>
        </TableWrapper>
      </EditableOnlyProvider>
    </TableProvider>
  );
};

const TableWrapper = styled.div`
  height: 100%;
  width: 100%;
  position: relative;
  top: 0;
  bottom: 0;
  right: 0;
  left: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

export const OuterContainer = styled.div<{ $variant?: TableVariant }>`
  position: relative;
  overflow: hidden;
  flex-grow: 1;
  background-color: ${p =>
    p.$variant === 'transparent' ? 'transparent' : p.theme.palette.bg.primary};
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const OverLay = styled.div<{ abs?: boolean; $headerHeight?: number }>`
  position: ${p => (p.abs ? 'absolute' : 'relative')};
  ${p =>
    p.abs &&
    `left: 0; right: 0; bottom: 0; top: ${
      p.$headerHeight ?? 0
    }px; width: 100%; height: calc(100% - ${p.$headerHeight ?? 0}px);`}
  z-index: 2;
  overflow: hidden;
  box-sizing: border-box;
  contain: layout paint;
`;

/** Fills overlay exactly; scrolls when zoomed. Content stays below header (overlay clips). */
const EmptyStateScrollRegion = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: auto;
  overflow-x: hidden;
  padding: ${p => p.theme.s.lg} ${p => p.theme.s.md} ${p => p.theme.s.xl};
  box-sizing: border-box;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
`;

/** Wraps empty state; content can grow so when zoomed user can scroll to see image and text. */
const EmptyStateInnerWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  margin: auto;
  flex-shrink: 0;
`;

export const Container = styled.div<{ $isEmpty?: boolean }>`
  flex: 1;
  min-height: 0;
  width: 100%;
  overflow-x: ${p => (p.$isEmpty ? 'hidden' : 'auto')};
  overflow-y: ${p => (p.$isEmpty ? 'hidden' : 'auto')};
`;

export const TableStyles = styled.table<{
  fillerPosition: number;
  remainingWidth: number;
  rowHeight: number;
}>`
  width: 100%;
  border-spacing: 0px;

  .frozen {
    position: sticky;
    display: flex;
    align-items: center;
    z-index: 4;
  }
  th.frozen > div {
    width: 100%;
  }

  tr {
    display: flex;
    width: fit-content;
    height: ${p => p.rowHeight}px;
  }

  tr th:last-child {
    border-top: 0;
  }
`;

export default Table;
